import { APP_VERSION } from '../config/appVersion';
import { BRAND_LOGOS } from '../config/brandLogos';
import { applyConsoleFilter, appLogger } from '../utils/appLogger';
import { bootstrapPaymentReturn } from '../utils/paymentReturnBootstrap';

document.documentElement.dataset.appVersion = APP_VERSION;
if (typeof window !== 'undefined') {
  (window as Window & { __GGJ_APP_VERSION__?: string }).__GGJ_APP_VERSION__ = APP_VERSION;
}

// Restrict console to warn/error when CASHFREE_ENV=PROD (show all when TEST)
applyConsoleFilter();
bootstrapPaymentReturn();

import { App as CapApp } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { Keyboard } from '@capacitor/keyboard';
import { StatusBar, Style } from '@capacitor/status-bar';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
// Native Android/iOS bootstrap — only runs inside the Capacitor shell
if (Capacitor.isNativePlatform()) {
  document.documentElement.classList.add('ggj-native');
  // Apply theme class immediately so first paint matches light/dark (no white flash)
  const _savedTheme = (() => { try { return localStorage.getItem('app-theme'); } catch { return null; } })();
  const _hour = new Date().getHours();
  const _timeLight = _hour >= 6 && _hour < 18;
  const _isLight = _savedTheme === 'light' || (_savedTheme !== 'dark' && _timeLight);
  document.documentElement.classList.remove('light', 'dark');
  document.documentElement.classList.add(_isLight ? 'light' : 'dark');
  StatusBar.setStyle({ style: _isLight ? Style.Light : Style.Dark }).catch(() => {});
  StatusBar.setBackgroundColor({ color: _isLight ? '#8B80F9' : '#19191A' }).catch(() => {});

  // Keyboard: prevent the layout from jumping when the soft keyboard opens
  Keyboard.setAccessoryBarVisible({ isVisible: false }).catch(() => {});

  // Back button: dispatch a custom event so the React app handles navigation.
  // We do NOT call window.history.back() — on Capacitor the WebView history stack
  // starts at https://localhost/ (the marketing page), so going back via the browser
  // API would navigate away from the dashboard and show the landing page.
  CapApp.addListener('backButton', ({ canGoBack }) => {
    // Let the React app decide what "back" means (pop nav stack, exit step, etc.)
    const handled = window.dispatchEvent(new CustomEvent('capacitor-back-button', { cancelable: true }));
    // Only exit the app if no React handler consumed the event AND there's nowhere to go
    if (handled && !canGoBack) {
      CapApp.exitApp();
    }
  });

  // OAuth deep-link handler — catches com.gograbjob.app://auth/callback
  // after Google/LinkedIn redirect back to the app.
  // Supabase puts access_token + refresh_token in the URL hash or query string.
  CapApp.addListener('appUrlOpen', async (event: { url: string }) => {
    try {
      const url = event.url;
      appLogger.info('[OAuth] appUrlOpen received:', url);

      // Extract token string from hash (#) or query (?)
      let tokenString = '';
      if (url.includes('#')) {
        tokenString = url.split('#')[1];
      } else if (url.includes('?')) {
        tokenString = url.split('?')[1];
      }

      if (!tokenString) {
        appLogger.warn('[OAuth] No token string found in callback URL');
        return;
      }

      const params = new URLSearchParams(tokenString);
      const accessToken = params.get('access_token');
      const refreshToken = params.get('refresh_token');

      if (accessToken && refreshToken) {
        // Inject a branded in-app overlay instead of the native splash so the
        // transition is theme-consistent and doesn't flash the white XML splash.
        const _overlay = document.createElement('div');
        _overlay.id = 'oauth-loading-overlay';
        _overlay.style.cssText = [
          'position:fixed;inset:0;z-index:99999',
          'background:var(--vscode-bg-primary,#19191A)',
          'display:flex;flex-direction:column;align-items:center;justify-content:center',
          'transition:opacity 0.25s ease',
        ].join(';');
        _overlay.innerHTML = `
          <style>
            #oauth-loading-overlay .brand-mark-light { display:block; width:72px; height:72px; object-fit:contain; }
            #oauth-loading-overlay .brand-mark-dark { display:none; width:72px; height:72px; object-fit:contain; }
            html.dark #oauth-loading-overlay .brand-mark-light { display:none; }
            html.dark #oauth-loading-overlay .brand-mark-dark { display:block; }
          </style>
          <div style="margin-bottom:20px;width:72px;height:72px;display:flex;align-items:center;justify-content:center">
            <img class="brand-mark-light" src="${BRAND_LOGOS.mark.light}" width="72" height="72" alt="GoGrabJob" draggable="false" />
            <img class="brand-mark-dark" src="${BRAND_LOGOS.mark.dark}" width="72" height="72" alt="GoGrabJob" draggable="false" />
          </div>
          <div style="font-size:11px;color:var(--vscode-text-tertiary,rgba(255,255,255,0.45));letter-spacing:0.08em;font-family:system-ui,sans-serif">Signing you in…</div>
        `;
        document.body.appendChild(_overlay);

        const _removeOverlay = () => {
          const el = document.getElementById('oauth-loading-overlay');
          if (!el) return;
          el.style.opacity = '0';
          setTimeout(() => el.parentNode?.removeChild(el), 260);
        };

        // Dynamically import supabase to avoid circular deps at boot time
        const { supabase } = await import('../lib/supabase');
        const { error } = await supabase.auth.setSession({
          access_token: accessToken,
          refresh_token: refreshToken,
        });
        if (error) {
          appLogger.error('[OAuth] setSession failed:', error.message);
          _removeOverlay();
        } else {
          appLogger.info('[OAuth] Session established from deep link');
          // Mark tab active so AppStateManager's session-invalidation check
          // (which fires on the next route evaluation) doesn't sign the user out.
          sessionStorage.setItem('tab_session_active', Date.now().toString());
          // Overlay fades out once MobileAppLoader mounts and React takes over.
          // Use a small safety timeout so it never gets stuck.
          setTimeout(_removeOverlay, 2000);
        }
      } else {
        appLogger.warn('[OAuth] access_token or refresh_token missing in callback URL');
      }
    } catch (err) {
      appLogger.error('[OAuth] appUrlOpen handler error:', err);
    }
  });

  // DO NOT hide splash here — MobileAppLoader does it after React renders
  // (so the native splash cross-fades directly into the React loader, no blank flash).

  // Measure env(safe-area-inset-top) and expose it as a CSS variable.
  // Some Android/Capacitor combos return 0 for env(); the variable gives a reliable fallback.
  requestAnimationFrame(() => {
    const probe = document.createElement('div');
    probe.style.cssText =
      'position:fixed;top:env(safe-area-inset-top,0px);left:0;width:0;height:0;pointer-events:none;visibility:hidden';
    document.body.appendChild(probe);
    const measured = Math.round(probe.getBoundingClientRect().top);
    document.body.removeChild(probe);
    const safeTop = measured > 8 ? measured : 28; // 28 px = typical Android status bar
    document.documentElement.style.setProperty('--safe-area-top', `${safeTop}px`);
  });
}
import { HelmetProvider } from 'react-helmet-async';

import { ThemeProvider } from '../contexts/ThemeContext.tsx';
import GlobalErrorHandler from '../shared/components/feedback/GlobalErrorHandler.tsx';

import '../i18n';
import { reportWebVitals } from '../shared/services/reportWebVitals';

import App from './App.tsx';
import '../assets/styles/vscode-theme-variables.css';
import '../assets/styles/index.css';
import '../assets/styles/landing/marketing-footer.css';
import '../assets/styles/landing/dark/marketing-footer-dark.css';
import '../assets/styles/landing/dark/home-landing-nav-dark.css';
import '../assets/styles/contact-page.css';

// Performance monitoring
const startTime = performance.now();

// Create root and render app
const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error('Root element not found');
}

createRoot(rootElement).render(
  <StrictMode>
    <HelmetProvider>
      <ThemeProvider>
        <GlobalErrorHandler>
          <App />
        </GlobalErrorHandler>
      </ThemeProvider>
    </HelmetProvider>
  </StrictMode>
);

reportWebVitals((metric) => {
  if (import.meta.env.DEV) {
    // Local Vite has no `/api/vitals` (Netlify edge handles it in deployed builds).
    console.log('[CWV]', metric);
    return;
  }

  const blob = new Blob([JSON.stringify(metric)], { type: 'application/json' });
  const queued =
    typeof navigator !== 'undefined' &&
    typeof navigator.sendBeacon === 'function' &&
    navigator.sendBeacon('/api/vitals', blob);
  if (!queued) {
    // sendBeacon missing or queue full — warn so PROD still surfaces CWV (console.log is no-oped by applyConsoleFilter)
    console.warn('[CWV]', metric);
  }
});

// The `ggj-prerender-ready` document event is dispatched from src/app/App.tsx
// once `appState.loading` flips to false. We deliberately DO NOT set a timer
// fallback here — doing so silently committed loading-spinner snapshots for
// routes whose Supabase session check took >8s (notably /about-us). Genuinely
// stuck routes are caught by the 60s `timeout` in vite.config.ts rendererOptions,
// which fails the build loudly instead of shipping empty HTML.

// Log initial render time (only in TEST; suppressed in PROD via applyConsoleFilter)
window.addEventListener('load', () => {
  const loadTime = performance.now() - startTime;
  appLogger.info('App loaded in', loadTime.toFixed(2), 'ms');
});
